import React from 'react';
import { Button, Dropdown, Empty, Spin, Tag } from 'antd';
import {
  MoreOutlined,
  RollbackOutlined,
  DiffOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { formatDateShort } from '../../pages/channels/utils/formatters';
import { useOverlayScrollbarRef } from '../../hooks/useOverlayScrollbar';
import './PromptEditor.css';

function formatAuthorName(createdBy) {
  if (!createdBy) return 'Unknown author';
  if (createdBy.includes('@')) return createdBy.split('@')[0];
  if (createdBy.length > 12) return `${createdBy.slice(0, 8)}…`;
  return createdBy;
}

export default function PromptHistoryPanel({
  versions = [],
  loading,
  onClose,
  liveVersionId,
  selectedVersionId,
  onSelectVersion,
  onRollback,
  onCompare,
}) {
  const historyBodyScrollRef = useOverlayScrollbarRef();

  const buildMenuProps = (v) => {
    const items = [];
    if (v.id !== liveVersionId) {
      items.push({
        key: 'compare',
        icon: <DiffOutlined />,
        label: 'Compare to draft',
      });
    }
    if (v.id !== liveVersionId && onRollback) {
      items.push({
        key: 'rollback',
        icon: <RollbackOutlined />,
        label: 'Rollback',
      });
    }
    if (items.length === 0) return null;

    return {
      items,
      onClick: ({ key, domEvent }) => {
        domEvent.stopPropagation();
        if (key === 'compare') onCompare?.(v.id);
        if (key === 'rollback') onRollback?.(v.id);
      },
    };
  };

  return (
    <aside className="prompt-history-panel" aria-label="Version history">
      <div className="prompt-history-panel-header">
        <span className="prompt-history-panel-title">Version history</span>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label="Close version history"
        />
      </div>

      <div className="prompt-history-panel-body" ref={historyBodyScrollRef}>
        {loading ? (
          <div className="prompt-history-panel-loading">
            <Spin />
          </div>
        ) : versions.length === 0 ? (
          <Empty description="No version history yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div className="prompt-history-list">
            {versions.map((v) => {
              const isSelected = v.id === selectedVersionId;
              const menuProps = buildMenuProps(v);
              const message = v.message && v.message !== 'Autosave' ? v.message : null;

              return (
                <div
                  key={v.id}
                  className={`prompt-history-item${isSelected ? ' is-selected' : ''}`}
                >
                  <div className="prompt-history-item-header">
                    <div
                      className="prompt-history-item-body"
                      role="button"
                      tabIndex={0}
                      onClick={() => onSelectVersion(v.id)}
                      onKeyDown={(e) => e.key === 'Enter' && onSelectVersion(v.id)}
                    >
                      <div className="prompt-history-item-row1">
                        <span className="prompt-history-item-date">
                          {v.created_at ? formatDateShort(v.created_at) : '—'}
                        </span>
                        <Tag color="blue" className="prompt-history-item-version">v{v.version_number}</Tag>
                        {v.id === liveVersionId && <Tag color="green">Current</Tag>}
                        {v.status === 'draft' && v.id !== liveVersionId && <Tag>Draft</Tag>}
                      </div>
                      {message && (
                        <div className="prompt-history-item-message">{message}</div>
                      )}
                      <div className="prompt-history-item-author">
                        {v.created_by_name || formatAuthorName(v.created_by)}
                      </div>
                    </div>
                    {menuProps && (
                      <Dropdown
                        menu={menuProps}
                        trigger={['click']}
                        getPopupContainer={() => document.body}
                        overlayStyle={{ zIndex: 1300 }}
                      >
                        <Button
                          type="text"
                          size="small"
                          icon={<MoreOutlined />}
                          className="prompt-history-item-menu"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Dropdown>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}
